import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { formatCurrencyColumns, getBase64FromLocalImage, sanitizeSheetName, styleRow } from '../utils';


const addSectionWorksheet = (workbook, section, logoBase64, sectionTotals) => {
  let baseName = sanitizeSheetName(section?.location || 'Sheet');
  let sheetName = baseName;
  let counter = 1;

  // Loop to find a unique name
  while (workbook.getWorksheet(sheetName)) {
    sheetName = `${baseName} ${counter}`;
    counter++;
  }

  const sheet = workbook.addWorksheet(sheetName);

  // Set columns (added S. No. at start and Remarks at end)
  sheet.columns = [
    { header: 'S. No.', key: 'sno', width: 8 },
    { header: 'Item Name', key: 'name', width: 30 },
    { header: 'Description', key: 'description', width: 40 },
    { header: 'Quantity', key: 'quantity', width: 12 },
    { header: 'Rate', key: 'rate', width: 15 },
    { header: 'Total Without GST', key: 'totalWithoutGST', width: 18 },
    { header: 'GST %', key: 'gstPercent', width: 12 },
    { header: 'GST Amount', key: 'gstAmount', width: 18 },
    { header: 'Total With GST', key: 'totalWithGST', width: 20 },
    { header: 'Remarks', key: 'remarks', width: 25 } // New column
  ];

  styleRow(sheet.getRow(1), true);

  const itemsByCategory = section.items.reduce((acc, item) => {
    const category = item.category || 'Uncategorized';
    if (!acc[category]) acc[category] = [];
    acc[category].push(item);
    return acc;
  }, {});

  let currentRow = 2;
  let serialNumber = 1;
  let sectionTotalWithoutGST = 0;
  let sectionGSTAmount = 0;
  let sectionTotalWithGST = 0;

  Object.entries(itemsByCategory).forEach(([category, items]) => {
    // Category header
    sheet.addRow([category, '', '', '', '', '', '', '', '', '']);
    const categoryRow = sheet.getRow(currentRow);
    styleRow(categoryRow, false, true, true);
    sheet.mergeCells(`A${currentRow}:J${currentRow}`); // Merge across 10 columns now
    currentRow++;

    items.forEach((item) => {
      // console.log(item.quantity, typeof item.quantity, 'quant')
      const quantity = Number(item.quantity) || 1;

      const rate = Number(item.rate);
      const totalWithoutGST = quantity * rate;
      const gstPercent = item.gst ?? 18;

      const gstAmount = totalWithoutGST * (gstPercent / 100);
      const totalWithGST = totalWithoutGST + gstAmount;

      const row = sheet.addRow({
        sno: serialNumber++,
        name: item.name || '',
        description: item.shortDescription || '',
        quantity: quantity,
        rate: rate,
        totalWithoutGST: { formula: `E${currentRow}*D${currentRow}`, result: totalWithoutGST },
        gstPercent: gstPercent,
        gstAmount: { formula: `F${currentRow}*G${currentRow}/100`, result: gstAmount },
        totalWithGST: { formula: `F${currentRow}+H${currentRow}`, result: totalWithGST },
        remarks: item.remarks || ''
      });

      // Lock formula cells
      [6, 8, 9].forEach(col => {
        sheet.getCell(row.number, col).protection = { locked: true };
      });

      sectionTotalWithoutGST += totalWithoutGST;
      sectionGSTAmount += gstAmount;
      sectionTotalWithGST += totalWithGST;

      styleRow(sheet.getRow(currentRow));
      currentRow++;
    });
  });

  // Section Total Row
  const totalRow = sheet.addRow({
    sno: '',
    name: 'SECTION TOTAL',
    totalWithoutGST: { formula: `SUM(F2:F${currentRow - 1})`, result: sectionTotalWithoutGST },
    gstAmount: { formula: `SUM(H2:H${currentRow - 1})`, result: sectionGSTAmount },
    totalWithGST: { formula: `SUM(I2:I${currentRow - 1})`, result: sectionTotalWithGST }
  });
  for (let i = 1; i <= 10; i++) {
    if (!totalRow.getCell(i).value) {
      totalRow.getCell(i).value = '';
    }
  }

  [6, 8, 9].forEach(col => {
    sheet.getCell(totalRow.number, col).protection = { locked: true };
  });

  styleRow(totalRow, false, true);
  formatCurrencyColumns(sheet, ['E', 'F', 'H', 'I']);

  for (let i = 2; i < currentRow; i++) {
    sheet.getCell(`G${i}`).numFmt = '0"%";-0"%";0"%"';
  }

  // sheet.protect('ppbom', {
  //   selectLockedCells: true,
  //   selectUnlockedCells: true,
  //   formatCells: false,
  //   insertColumns: false,
  //   insertRows: false,
  //   deleteColumns: false,
  //   deleteRows: false,
  // });

  sectionTotals.push({
    name: sheet.name,
    totalBeforeGST: sectionTotalWithoutGST,
    gst: sectionGSTAmount,
    totalAfterGST: sectionTotalWithGST,
    totalRowNumber: totalRow.number,
    quantity: section?.quantity || 1
  });

  return sheet;
};


const addSummaryTable = (sheet, sectionTotals, startRow = 1) => {
  const headers = [
    'S. No.', 'Section', 'Quantity', 'Rate', 'Total Without GST', 'GST Amount', 'Total With GST'
  ];

  const columnWidths = [10, 25, 12, 15, 20, 20, 20];

  // Set column widths
  columnWidths.forEach((width, i) => {
    sheet.getColumn(i + 1).width = width;
  });

  // Write header row
  const headerRow = sheet.getRow(startRow);
  headers.forEach((h, i) => headerRow.getCell(i + 1).value = h);
  styleRow(headerRow, true);

  let currentRow = startRow + 1;

  // Fill section rows
  sectionTotals.forEach((section, idx) => {
    const quantity = section.quantity || 1;
    const row = sheet.getRow(currentRow);

    row.getCell(1).value = idx + 1; // S. No.
    row.getCell(2).value = section.name;
    row.getCell(3).value = quantity;

    row.getCell(4).value = {
      formula: `'${section.name}'!F${section.totalRowNumber}`,
      result: section.totalBeforeGST
    };
    row.getCell(5).value = {
      formula: `C${currentRow}*D${currentRow}`,
      result: section.totalBeforeGST * quantity
    };
    row.getCell(6).value = {
      formula: `C${currentRow}*'${section.name}'!H${section.totalRowNumber}`,
      result: section.gst * quantity
    };
    row.getCell(7).value = {
      formula: `E${currentRow}+F${currentRow}`,
      result: section.totalAfterGST * quantity
    };

    [4, 5, 6, 7].forEach(col => {
      row.getCell(col).protection = { locked: true };
    });

    styleRow(row);
    currentRow++;
  });

  // Calculate actual totals
  let grandTotalWithoutGST = 0;
  let grandGST = 0;
  let grandTotalWithGST = 0;

  sectionTotals.forEach(section => {
    const quantity = section.quantity || 1;
    grandTotalWithoutGST += section.totalBeforeGST * quantity;
    grandGST += section.gst * quantity;
    grandTotalWithGST += section.totalAfterGST * quantity;
  });

  // Add GRAND TOTAL row
  const totalRow = sheet.getRow(currentRow);
  for (let i = 1; i <= 7; i++) {
    if (!totalRow.getCell(i).value) {
      totalRow.getCell(i).value = '';
    }
  }
  totalRow.getCell(2).value = 'GRAND TOTAL';

  totalRow.getCell(5).value = {
    formula: `SUM(E${startRow + 1}:E${currentRow - 1})`,
    result: grandTotalWithoutGST
  };
  totalRow.getCell(6).value = {
    formula: `SUM(F${startRow + 1}:F${currentRow - 1})`,
    result: grandGST
  };
  totalRow.getCell(7).value = {
    formula: `SUM(G${startRow + 1}:G${currentRow - 1})`,
    result: grandTotalWithGST
  };

  [5, 6, 7].forEach(col => {
    totalRow.getCell(col).protection = { locked: true };
  });

  styleRow(totalRow, false, true);
  formatCurrencyColumns(sheet, ['D', 'E', 'F', 'G']); // Include 'D' for rate

  return currentRow;
};





const addTermsAndConditions = (sheet, terms, lastDataRow, salesPerson) => {
  const termsStartRow = lastDataRow;
  const termsArray = terms.split('\n');

  termsArray.forEach((term, idx) => {
    const row = sheet.addRow([term]);
    const rowNumber = termsStartRow + 1 + idx;

    sheet.mergeCells(`A${rowNumber}:G${rowNumber}`);
    row.height = 25;

    row.eachCell(cell => {
      cell.font = {
        name: 'Calibri',
        size: idx === 0 ? 12 : 10,
        bold: idx === 0,
        color: { argb: 'FF404040' },
      };
      cell.alignment = {
        wrapText: true,
        vertical: 'middle',
        horizontal: 'left',
      };

      if (idx === 0) {
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FFFDE9D9' },
        };
      }
    });
  });

  const spacingRow = sheet.addRow([]);
  spacingRow.height = 15;

  // "With regards" Row
  const withRegardsRow = sheet.addRow(['With Regards,']);
  const withRegardsRowNum = withRegardsRow.number;
  sheet.mergeCells(`A${withRegardsRowNum}:R${withRegardsRowNum}`);
  withRegardsRow.height = 25;
  withRegardsRow.getCell(1).font = {
    name: 'Calibri',
    size: 12,
    italic: true,
    color: { argb: 'FF333333' },
  };
  withRegardsRow.getCell(1).alignment = {
    vertical: 'middle',
    horizontal: 'left',
  };

  // Signature Block
  const fields = [
    [salesPerson?.name],
    [salesPerson?.phoneNo],
    [salesPerson?.email],
    // ['Designation', salesPerson?.designation],
    // ['Department', salesPerson?.department],

  ];

  fields.forEach(([label, value]) => {
    const row = sheet.addRow([`${label}`]);
    const rowNum = row.number;
    sheet.mergeCells(`A${rowNum}:R${rowNum}`);
    row.height = 22;

    row.eachCell(cell => {
      cell.font = {
        name: 'Calibri',
        size: 10,
        color: { argb: 'FF555555' },
      };
      cell.alignment = {
        vertical: 'middle',
        horizontal: 'left',
      };
    });
  });
};



const addCompanyInfo = (sheet, logoBase64, company, customer, quotationId) => {
  const logoRowCount = 4;
  const spacingAfterLogo = 1;

  const companyName = company?.name || '';
  const address = company?.address?.replace(/\\n/g, '\n') || '';
  const web = company?.web || '';
  const gstNo = company?.gstno ? `GST No: ${company.gstno}` : '';

  const companyInfo = [
    ...address.split('\n').map(line => line.trim()),
    gstNo,
    web
  ].filter(Boolean);
  const customerInfo = [
    `To: ${customer?.name || ''}`,
    ` ${customer?.address || ''}`,
    `GST No: ${customer?.gstno || ''}`,
    `Contact Person: ${customer?.contactPerson || ''}`,
    `Mobile No: ${customer?.mobileNo || ''}`,
  ];


  const quotationInfo = [
    `Quotation No.: ${quotationId}`,
    `Date: ${new Date().toLocaleDateString()}`
  ];

  const totalHeaderRows =
    logoRowCount + spacingAfterLogo + companyInfo.length + 1 +
    Math.max(customerInfo.length, quotationInfo.length);

  // Insert empty rows at top
  sheet.spliceRows(1, 0, ...new Array(totalHeaderRows).fill([]));

  // Clear logo area
  for (let r = 1; r <= logoRowCount; r++) {
    const row = sheet.getRow(r);
    for (let c = 1; c <= 7; c++) {
      row.getCell(c).value = null;
    }
  }

  // Insert logo (centered visually)
  sheet.mergeCells(`A1:G${logoRowCount}`);
  const logoId = sheet.workbook.addImage({ base64: logoBase64, extension: 'png' });
  sheet.addImage(logoId, {
    tl: { col: 6, row: 0.5 },
    ext: { width: 150, height: 50 },
  });

  // Company Name row
  const companyNameRow = logoRowCount + 1;
  const nameRow = sheet.getRow(companyNameRow);
  sheet.mergeCells(`A${companyNameRow}:G${companyNameRow}`);
  nameRow.getCell(1).value = companyName;
  nameRow.height = 25;
  nameRow.eachCell(cell => {
    cell.font = { name: 'Calibri', size: 14, bold: true, color: { argb: 'FF1F497D' } };
    cell.alignment = { vertical: 'middle', horizontal: 'center' };
  });

  // Company info
  companyInfo.forEach((info, i) => {
    const rowNum = companyNameRow + 1 + i;
    const row = sheet.getRow(rowNum);
    sheet.mergeCells(`A${rowNum}:G${rowNum}`);
    row.getCell(1).value = info;
    row.height = 18;
    row.eachCell(cell => {
      cell.font = { name: 'Calibri', size: 10 };
      cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
    });
  });

  const infoStartRow = companyNameRow + companyInfo.length + spacingAfterLogo + 1;

  // Style for info cells
  const cellStyle = {
    // font: { name: 'Calibri', size: 10 },
    // // fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF7F7F7' } },
    // alignment: { vertical: 'middle', horizontal: 'left' },
    // border: {
    //   top: { style: 'thin', color: { argb: 'FFCCCCCC' } },
    //   left: { style: 'thin', color: { argb: 'FFCCCCCC' } },
    //   bottom: { style: 'thin', color: { argb: 'FFCCCCCC' } },
    //   right: { style: 'thin', color: { argb: 'FFCCCCCC' } },
    // }
  };

  const maxRows = Math.max(customerInfo.length, quotationInfo.length + 1); // +1 for the extra blank row
  for (let i = 0; i < maxRows; i++) {
    const rowNum = infoStartRow + i;
    const row = sheet.getRow(rowNum);
    row.height = 20;

    // CUSTOMER INFO: No blank row logic here
    if (i < customerInfo.length) {
      sheet.mergeCells(`A${rowNum}:C${rowNum}`);
      const leftCell = row.getCell(1); // A
      leftCell.value = customerInfo[i];
      leftCell.style = {
        ...cellStyle,
        alignment: { ...cellStyle.alignment, horizontal: 'left' }
      };
    }

    // QUOTATION INFO: Insert blank row after first element (index 0)
    const qIndex = i === 0 ? 0 : i === 1 ? -1 : i - 1; // skip index 1 for blank row
    if (qIndex >= 0 && qIndex < quotationInfo.length) {
      const rightCell = row.getCell(7); // G
      rightCell.value = quotationInfo[qIndex];
      rightCell.style = {
        ...cellStyle,
        alignment: { ...cellStyle.alignment, horizontal: 'right' }
      };
    }
  }



  return infoStartRow + Math.max(customerInfo.length, quotationInfo.length) - 1;
};







export const exportToExcel = async (data, company, setLoading, navigate) => {
  try {
    setLoading(true);

    const workbook = new ExcelJS.Workbook();
    workbook.creator = 'Invoice System';
    workbook.created = new Date();
    workbook.modified = new Date();

    const logoBase64 = await getBase64FromLocalImage('https://pp.zaptas.in/logo1.2.png');

    const sectionTotals = [];
    data?.data.forEach(section => {
      addSectionWorksheet(workbook, section, logoBase64, sectionTotals);
    });

    const summarySheet = workbook.addWorksheet('Summary');

    const lastCompanyRow = addCompanyInfo(
      summarySheet,
      logoBase64,
      company[0],
      data?.customer,
      data?.quotationId
    );

    const summaryTableStartRow = lastCompanyRow + 2;
    for (let i = 0; i < 2; i++) {
      summarySheet.getRow(lastCompanyRow + i + 1).values = [];
    }

    const lastSummaryRow = addSummaryTable(summarySheet, sectionTotals, summaryTableStartRow);

    const termsStartBaseRow = lastSummaryRow + 2;
    for (let i = 0; i < 2; i++) {
      summarySheet.getRow(lastSummaryRow + i + 1).values = [];
    }

    addTermsAndConditions(summarySheet, data?.terms, termsStartBaseRow, data?.salesData);

    summarySheet.orderNo = 0;

    let orderIndex = 1;
    workbook.worksheets.forEach(sheet => {
      if (sheet !== summarySheet) {
        sheet.orderNo = orderIndex++;
      }
    });

    const buffer = await workbook.xlsx.writeBuffer();

    saveAs(
      new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      }),
      `Invoice_Export_${new Date().toISOString().slice(0, 10)}.xlsx`
    );

    setLoading(false);
  } catch (error) {
    console.error("Excel export failed:", error);
    setLoading(false);
    alert("An error occurred while generating the Excel sheet. Please contact the developer.");
    navigate("/");
  }
};
